/**
 * Kid sign-in session.
 *
 * Tracks which child profile is active (passwordless avatar pick) and whether
 * the guardian PIN area is unlocked. Selection is mirrored on the server via
 * the auth cookie; this context caches it for the UI.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { SessionContext, type SessionState } from "@/app/sessionContext";
import { auth, children as childrenApi, type Child } from "@/lib/api";

export function SessionProvider({ children }: { children: ReactNode }) {
  const [childrenList, setChildrenList] = useState<Child[]>([]);
  const [activeChild, setActiveChild] = useState<Child | null>(null);
  const [guardianUnlocked, setGuardianUnlocked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [kids, session] = await Promise.all([
          childrenApi.list().catch(() => [] as Child[]),
          auth.session().catch(() => ({ childId: null, guardianUnlocked: false })),
        ]);
        if (!active) return;
        setChildrenList(kids);
        setGuardianUnlocked(session.guardianUnlocked);
        setActiveChild(kids.find((c) => c.id === session.childId) ?? null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const selectChild = useCallback(
    async (childId: string) => {
      await auth.selectChild(childId);
      setActiveChild(childrenList.find((c) => c.id === childId) ?? null);
    },
    [childrenList],
  );

  const unlockGuardian = useCallback(async (pin: string) => {
    try {
      const session = await auth.unlockGuardian(pin);
      setGuardianUnlocked(session.guardianUnlocked);
      return session.guardianUnlocked;
    } catch {
      return false;
    }
  }, []);

  const signOut = useCallback(async () => {
    await auth.signOut().catch(() => undefined);
    setActiveChild(null);
    setGuardianUnlocked(false);
  }, []);

  const value = useMemo<SessionState>(
    () => ({
      childrenList,
      activeChild,
      guardianUnlocked,
      loading,
      selectChild,
      unlockGuardian,
      signOut,
    }),
    [
      childrenList,
      activeChild,
      guardianUnlocked,
      loading,
      selectChild,
      unlockGuardian,
      signOut,
    ],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}
